import React, { useEffect, useRef, useState } from 'react';
import { Mic, Pause, Play, Square, Upload, Loader2, ShieldCheck } from 'lucide-react';
import { api, DEMO } from '../lib/api';

// Soft ceiling only — the recorder never auto-stops; it just warns so a long dictation
// isn't silently lost. The endpoint's hard cap (250 MB, ~4h of opus) is far above this.
const SOFT_LIMIT = 200 * 1024 * 1024;

const DICTATION_SAMPLES = {
  soap: 'Patient encounter review. Follow-up for recurrent major depression and generalized anxiety. Reports good compliance with daily morning medication and improved sleep pattern. Denies suicidal or homicidal thoughts. Mental status: calm, oriented x4, mood euthymic, thoughts linear. Plan: Continue current medication dosage. Re-evaluate in 4 weeks.',
  psychiatric_evaluation: 'Psychiatric evaluation. Patient referred for comprehensive evaluation of depressive symptoms and functional impairment. Past history reviewed. Mental status examination alert and oriented x4. Recommend PHP admission for multidisciplinary stabilization.',
  bh_progress_note: 'Daily behavioral health progress note. Patient present and engaged constructively in scheduled group CBT exercises. Adherence to prescribed medication verified. Denies acute safety concerns.',
};

// Dictation for a shared AI-note instruction: record (or upload) audio → transcribe via
// the gateway (mode='transcript') → hand the text back through onText.
export default function DictationRecorder({ templateId, disabled, onText, notify }) {
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState('idle'); // idle|requesting|recording|paused|ready|transcribing
  const [seconds, setSeconds] = useState(0), [blob, setBlob] = useState(null), [bytes, setBytes] = useState(0);
  const recorder = useRef(null), stream = useRef(null), mounted = useRef(true), warned = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (recorder.current?.state !== 'inactive') recorder.current?.stop();
      stream.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  useEffect(() => {
    if (state !== 'recording') return;
    const t = setInterval(() => setSeconds(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  const pickMime = () => ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'].find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t));
  const needsTemplate = !templateId;

  async function start() {
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        throw new Error('Audio recording is unavailable in this browser. Use HTTPS or localhost and allow microphone access.');
      }
      setState('requesting');
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mounted.current) { s.getTracks().forEach(t => t.stop()); return; }
      stream.current = s;
      const mime = pickMime();
      const r = mime ? new MediaRecorder(s, { mimeType: mime }) : new MediaRecorder(s);
      recorder.current = r;
      const chunks = [];
      let total = 0;
      r.ondataavailable = e => {
        if (!e.data.size) return;
        chunks.push(e.data);
        total += e.data.size;
        setBytes(total);
        if (total > SOFT_LIMIT && !warned.current) {
          warned.current = true;
          notify('Recording is very long and may approach the transcription size limit. Consider pausing to review.');
        }
      };
      r.onstop = () => {
        s.getTracks().forEach(t => t.stop());
        if (mounted.current) {
          const recordedBlob = new Blob(chunks, { type: r.mimeType });
          setBlob(recordedBlob);
          // In demo mode or if no gateway text, automatically capture transcription upon stop
          if (DEMO) {
            const sample = DICTATION_SAMPLES[templateId] || DICTATION_SAMPLES.bh_progress_note;
            onText(sample);
            notify('Audio dictation captured and transcribed into instruction box.');
          }
        }
      };
      r.onerror = () => {
        s.getTracks().forEach(t => t.stop());
        if (mounted.current) {
          setState('idle');
          notify('Recording failed. Please try again.');
        }
      };
      r.start(1000);
      setBlob(null);
      setBytes(0);
      setSeconds(0);
      warned.current = false;
      setState('recording');
    } catch (e) {
      if (mounted.current) {
        setState('idle');
        notify(e.message);
      }
    }
  }

  function onFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > 250000000) { notify('Audio must be under 250 MB.'); return; }
    setBlob(f);
    setBytes(f.size);
    setState('ready');
  }

  async function transcribe() {
    if (!blob) return;
    if (!consent) { notify('Confirm consent before transcribing.'); return; }
    if (DEMO) {
      const sample = DICTATION_SAMPLES[templateId] || DICTATION_SAMPLES.bh_progress_note;
      onText(sample);
      notify('Audio dictation transcribed (demo).');
      setState('idle');
      return;
    }
    setState('transcribing');
    try {
      const form = new FormData();
      form.append('audio', blob, blob.name || 'dictation.webm');
      form.append('templateId', templateId);
      form.append('consent', 'true');
      const submitted = await api('/ai-notes/transcribe', { method: 'POST', body: form });
      const job = submitted.job;
      const deadline = Date.now() + 60 * 60 * 1000;
      for (;;) {
        if (Date.now() > deadline) throw new Error('Transcription is taking longer than expected.');
        await new Promise(r => setTimeout(r, 4000));
        const st = await api(`/ai-notes/transcribe/${job}`);
        if (st.status === 'ready') {
          if (mounted.current) setState('idle');
          onText(st.text || '');
          notify('Audio dictation transcribed successfully.');
          return;
        }
        if (st.status === 'failed') throw new Error('Transcription failed. Submit the recording again.');
      }
    } catch (e) {
      if (mounted.current) {
        setState('idle');
        notify(e.message);
      }
    }
  }

  const active = state === 'recording' || state === 'paused';
  return (
    <section className="ambient-card eval-recorder">
      <div className="section-title">
        <span className="purple-icon"><Mic size={20}/></span>
        <div>
          <h3>Dictate the instruction <span className="badge purple">VOICE</span></h3>
          <p>Speak the shared instruction; it is transcribed into the instruction box.</p>
        </div>
      </div>
      <label className="consent-check">
        <input type="checkbox" checked={consent} disabled={active || state === 'transcribing'} onChange={e => setConsent(e.target.checked)}/>
        I consent to recording and processing my dictation.
      </label>
      {needsTemplate && <p className="empty-copy">Choose a note template first, then record your instruction.</p>}
      <div className={`recorder ${state === 'recording' ? 'recording' : ''}`}>
        <span className="mic-circle"><Mic size={20}/></span>
        <div className="recording-label">
          <strong>
            {active
              ? (state === 'paused' ? 'Dictation paused' : 'Listening…')
              : blob
              ? 'Recording ready'
              : state === 'transcribing'
              ? 'Transcribing…'
              : 'Ready to dictate'}
          </strong>
          <span>
            {bytes
              ? `${(bytes / 1048576).toFixed(1)} MB captured — stays in memory until transcribed.`
              : 'Audio stays in memory until you transcribe it.'}
          </span>
        </div>
        <div className="waveform">
          {Array.from({ length: 22 }, (_, i) => (
            <i key={i} style={{ height: `${8 + (i * 7) % 24}px`, animationDelay: `${i * .04}s` }}/>
          ))}
        </div>
        <span className="timer">
          {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
        </span>
        {active ? (
          <>
            <button
              className="icon-button"
              aria-label={state === 'paused' ? 'Resume' : 'Pause'}
              onClick={() => {
                state === 'paused' ? recorder.current.resume() : recorder.current.pause();
                setState(state === 'paused' ? 'recording' : 'paused');
              }}
            >
              {state === 'paused' ? <Play size={17}/> : <Pause size={17}/>}
            </button>
            <button
              className="button record-button"
              onClick={() => {
                recorder.current.stop();
                setState('ready');
              }}
            >
              <Square size={13}/>Stop
            </button>
          </>
        ) : (
          <button
            className="button record-button"
            disabled={disabled || needsTemplate || state === 'transcribing' || state === 'requesting'}
            onClick={start}
          >
            <Mic size={15}/>{state === 'requesting' ? 'Connecting…' : 'Record instruction'}
          </button>
        )}
      </div>
      <div className="ambient-footer">
        <span>
          <ShieldCheck size={13}/>{DEMO ? 'Demo · Memory Isolated' : 'Sent only to the configured gateway'}
        </span>
        <div className="row-actions">
          <label className="text-button" style={{ cursor: 'pointer' }}>
            <Upload size={13}/>Upload audio
            <input type="file" accept="audio/*,video/webm,video/mp4" style={{ display: 'none' }} onChange={onFile}/>
          </label>
          <button
            disabled={!blob || active || disabled || needsTemplate || state === 'transcribing'}
            onClick={transcribe}
          >
            {state === 'transcribing' ? <Loader2 size={13} className="spin"/> : <Mic size={13}/>}
            {state === 'transcribing' ? 'Transcribing…' : 'Transcribe'}
          </button>
        </div>
      </div>
    </section>
  );
}
